import { useState, useEffect, useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Loader2, Copy, AlertCircle, CheckCircle2, Check, ChevronsUpDown, Search, Info, X } from 'lucide-react';
import { useCopyProjectData, CopyProjectDataResult } from '@/hooks/projects/useCopyProjectData';
import { supabase } from '@/integrations/supabase/client';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { 
  parseSearchQuery, 
  buildSupabaseSearchFilter, 
  matchesAllKeywords, 
  shouldExcludeItem 
} from '@/utils/searchQueryBuilder';

// Maximum specs that can be manually selected (does not apply to "Tất cả" mode)
const MAX_SELECTABLE_SPECS = 50;

interface SourceLayer {
  id: string;
  name: string;
  order_index: number;
  specs_count: number;
}


interface SourceSpec {
  id: string;
  code: string;
  type: string;
  location: string | null;
  description: string | null;
  image_url: string | null;
  project_id: string;
  project_name: string;
}

interface ProjectCopyDataDialogProps {
  projectId: string;
  projectName: string;
  isOpen: boolean;
  onClose: () => void;
}

interface SourceProjectStats {
  itemsCount: number;
  specsCount: number;
  drawingsCount: number;
}

// Virtual Scroll Component for Specs List
interface SpecsVirtualListProps {
  specs: SourceSpec[];
  selectedSpecIds: string[];
  setSelectedSpecIds: React.Dispatch<React.SetStateAction<string[]>>;
  debouncedSpecsSearch: string;
  maxSelectable: number;
  totalCount?: number;
  sourceProjectId: string;
  setSourceProjectId: (id: string) => void;
}

function SpecsVirtualList({ specs, selectedSpecIds, setSelectedSpecIds, debouncedSpecsSearch, maxSelectable, totalCount, sourceProjectId, setSourceProjectId }: SpecsVirtualListProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  
  const virtualizer = useVirtualizer({
    count: specs.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 44,
    overscan: 5,
  });

  return (
    <div 
      ref={parentRef} 
      className="h-[300px] overflow-auto border rounded"
    >
      {/* Bulk actions header */}
      {specs.length > 0 && (
        <div className="sticky top-0 bg-background z-10 flex items-center justify-between py-1 px-2 border-b">
          <span className="text-xs text-muted-foreground">
            {specs.length >= 100 
              ? `Hiển thị 100/${totalCount || 'nhiều'} kết quả - dùng tìm kiếm để thu hẹp`
              : `${specs.length} kết quả`
            }
          </span>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => {
              const specsToSelect = specs.slice(0, maxSelectable);
              // Auto-set sourceProjectId nếu chưa có (global search)
              if (!sourceProjectId && specsToSelect.length > 0 && specsToSelect[0].project_id) {
                setSourceProjectId(specsToSelect[0].project_id);
              }
              setSelectedSpecIds(specsToSelect.map(s => s.id));
            }}
            className="h-6 text-xs px-2"
          >
            Chọn tất cả hiển thị {specs.length > maxSelectable && `(tối đa ${maxSelectable})`}
          </Button>
        </div>
      )}
      
      {/* Virtual list container */}
      {specs.length > 0 && (
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualizer.getVirtualItems().map((virtualItem) => {
            const spec = specs[virtualItem.index];
            if (!spec) return null;
            
            return (
              <div
                key={spec.id}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${virtualItem.size}px`,
                  transform: `translateY(${virtualItem.start}px)`,
                }}
                className="flex items-center space-x-2 px-2"
              >
                <Checkbox 
                  id={`spec-${spec.id}`}
                  checked={selectedSpecIds.includes(spec.id)}
                  disabled={!selectedSpecIds.includes(spec.id) && selectedSpecIds.length >= maxSelectable}
                onCheckedChange={(checked) => {
                    if (checked) {
                      // Auto-set sourceProjectId nếu chưa có (global search)
                      if (!sourceProjectId && spec.project_id) {
                        setSourceProjectId(spec.project_id);
                      }
                      setSelectedSpecIds(prev => [...prev, spec.id]);
                    } else {
                      setSelectedSpecIds(prev => prev.filter(id => id !== spec.id));
                    }
                  }}
                />
                {spec.image_url && (
                  <img 
                    src={spec.image_url} 
                    alt="" 
                    className="h-8 w-8 rounded object-cover flex-shrink-0"
                  />
                )}
                <Label htmlFor={`spec-${spec.id}`} className="font-normal text-xs flex-1 cursor-pointer truncate">
                  <span className="font-medium">{spec.code}</span>
                  {spec.location && <span className="text-muted-foreground"> • {spec.location}</span>}
                  <span className="text-blue-600 dark:text-blue-400 ml-1 text-[11px]">
                    [{spec.project_name}]
                  </span>
                </Label>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Empty states */}
      {specs.length === 0 && debouncedSpecsSearch && (
        <div className="text-xs text-muted-foreground py-4 text-center">
          Không tìm thấy specs
        </div>
      )}
      {specs.length === 0 && !debouncedSpecsSearch && (
        <div className="text-xs text-muted-foreground py-4 text-center">
          Nhập từ khóa để tìm specs
        </div>
      )}
    </div>
  );
}

export function ProjectCopyDataDialog({ projectId, projectName, isOpen, onClose }: ProjectCopyDataDialogProps) {
  const [sourceProjectId, setSourceProjectId] = useState<string>('');
  const [projectSelectOpen, setProjectSelectOpen] = useState(false);
  const [copyItems, setCopyItems] = useState(false);
  const [copySpecs, setCopySpecs] = useState(true);
  const [copyDrawings, setCopyDrawings] = useState(false);
  const [copyLayersPins, setCopyLayersPins] = useState(true);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [sourceStats, setSourceStats] = useState<SourceProjectStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [copyResult, setCopyResult] = useState<CopyProjectDataResult | null>(null);
  
  // Selective layer state
  const [selectAllLayers, setSelectAllLayers] = useState(true);
  const [selectedLayerIds, setSelectedLayerIds] = useState<string[]>([]);

  // NEW: Selective specs state (Option B: Independent Search)
  const [specsSearchTerm, setSpecsSearchTerm] = useState('');
  const debouncedSpecsSearch = useDebouncedValue(specsSearchTerm, 300);
  const [selectAllSpecs, setSelectAllSpecs] = useState(false);
  const [selectedSpecIds, setSelectedSpecIds] = useState<string[]>([]);

  // Task 3: Use get_library_projects RPC to match Library's project access logic
  // This RPC includes 'archive' projects (unlike get_copy_accessible_projects)
  const { 
    data: projects, 
    isLoading: loadingProjects, 
    isError: projectsError, 
    refetch: refetchProjects 
  } = useQuery({
    queryKey: ['copy-library-projects'],
    queryFn: async () => {
      // Use get_library_projects to match Library module's access logic
      const { data, error } = await supabase.rpc('get_library_projects');
      
      if (!error && data && data.length > 0) {
        console.log('[Copy Dialog] get_library_projects returned', data.length, 'projects');
        return (data || []).map((p: { id: string; project_name: string; company: string | null; status: string }) => ({
          id: p.id,
          name: p.project_name,
          company: p.company,
          status: p.status,
          includeInGlobalReports: true
        }));
      }
      
      // Fallback: query with RLS (user chỉ thấy projects có quyền)
      console.warn('[Copy Dialog] RPC failed or empty, falling back:', error?.message);
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('projects')
        .select('id, project_name, company, status')
        .eq('is_active', true)
        .not('status', 'eq', 'inactive')
        .order('project_name');
        
      if (fallbackError) {
        console.error('[Copy Dialog] Fallback also failed:', fallbackError);
        throw fallbackError;
      }
      
      console.log('[Copy Dialog] Fallback returned', fallbackData?.length || 0, 'projects');
      return (fallbackData || []).map((p) => ({
        id: p.id,
        name: p.project_name,
        company: p.company,
        status: p.status,
        includeInGlobalReports: true
      }));
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
  });
  const copyMutation = useCopyProjectData();

  // Fetch source project layers with specs count for selective copying
  const { data: sourceLayers, isLoading: loadingLayers } = useQuery({
    queryKey: ['copy-source-layers', sourceProjectId],
    queryFn: async (): Promise<SourceLayer[]> => {
      const { data: layers, error } = await supabase
        .from('diary_specs_layers')
        .select('id, name, order_index')
        .eq('project_id', sourceProjectId)
        .order('order_index');
      if (error) throw error;
      
      // Fetch specs count per layer via pins
      const layersWithCount = await Promise.all(
        (layers ?? []).map(async (layer) => {
          // First get pins for this layer
          const { data: pins } = await supabase
            .from('diary_pins')
            .select('id')
            .eq('specs_layer_id', layer.id);
          
          const pinIds = pins?.map(p => p.id) ?? [];
          
          let specsCount = 0;
          if (pinIds.length > 0) {
            const { count } = await supabase
              .from('diary_spec_items')
              .select('id', { count: 'exact', head: true })
              .eq('project_id', sourceProjectId)
              .in('pin_id', pinIds)
              .is('deleted_at', null);
            specsCount = count || 0;
          }
          
          return { ...layer, specs_count: specsCount };
        })
      );
      return layersWithCount;
    },
    enabled: !!sourceProjectId && copySpecs && copyLayersPins,
  });

  // Filter out current project from source options
  const availableProjects = projects?.filter(p => p.id !== projectId && p.status !== 'inactive') || [];

  // Get all project IDs user can copy FROM (exclude target project)
  const copyableProjectIds = useMemo(() => 
    availableProjects.map(p => p.id),
    [availableProjects]
  );

  // Task 1: Fetch source specs - filter by sourceProjectId when selected, otherwise all projects
  const { data: sourceSpecsData, isLoading: loadingSpecs } = useQuery({
    queryKey: ['copy-source-specs', sourceProjectId, copyableProjectIds.length, debouncedSpecsSearch],
    queryFn: async (): Promise<{ specs: SourceSpec[]; totalCount: number }> => {
      // When sourceProjectId selected → only search that project
      const projectFilter = sourceProjectId 
        ? [sourceProjectId] 
        : copyableProjectIds;
      
      if (projectFilter.length === 0) {
        return { specs: [], totalCount: 0 };
      }
      
      // Query WITHOUT projects!inner JOIN to avoid RLS filter on projects table
      // This matches Library behavior - fetch specs directly, get project_name from projectsMap
      let query = supabase
        .from('diary_spec_items')
        .select(`
          id, code, type, location, description, image_url, project_id
        `, { count: 'exact' })
        .in('project_id', projectFilter)
        .is('deleted_at', null)
        .order('code');
      
      // Apply search filter (same logic as Library)
      const parsed = parseSearchQuery(debouncedSpecsSearch);
      if (parsed.keywords.length > 0) {
        const { include } = buildSupabaseSearchFilter(parsed, ['code', 'location', 'description']);
        if (include) query = query.or(include);
      }
      
      const { data, error, count } = await query.limit(100);
      if (error) throw error;
      
      // Build projectsMap from projects array (loaded via RPC, bypasses RLS)
      const projectsMap = new Map(projects?.map(p => [p.id, p.name]) || []);
      
      // Transform with project_name from projectsMap (not from JOIN)
      let specs: SourceSpec[] = (data || []).map(item => ({
        id: item.id,
        code: item.code,
        type: item.type,
        location: item.location,
        description: item.description,
        image_url: item.image_url,
        project_id: item.project_id,
        project_name: projectsMap.get(item.project_id) || 'Unknown',
      }));
      
      // Client-side filtering for intersection/exclusion
      if (parsed.type === 'intersection' && parsed.keywords.length > 1) {
        specs = specs.filter(s => matchesAllKeywords(s as unknown as Record<string, unknown>, parsed.keywords, ['code', 'location', 'description']));
      }
      if (parsed.excluded.length > 0) {
        specs = specs.filter(s => !shouldExcludeItem(s as unknown as Record<string, unknown>, parsed.excluded, ['code', 'location', 'description']));
      }
      
      return { specs, totalCount: count || 0 };
    },
    enabled: (sourceProjectId || copyableProjectIds.length > 0) && copySpecs && !selectAllSpecs,
  });

  // Load stats when source project changes
  useEffect(() => {
    if (!sourceProjectId) {
      setSourceStats(null);
      return;
    }

    const loadStats = async () => {
      setLoadingStats(true);
      try {
        const [itemsRes, specsRes, drawingsRes] = await Promise.all([
          supabase.from('items').select('id', { count: 'exact', head: true })
            .eq('project_id', sourceProjectId)
            .is('deleted_at', null),
          supabase.from('diary_spec_items').select('id', { count: 'exact', head: true })
            .eq('project_id', sourceProjectId)
            .is('deleted_at', null),
          supabase.from('drawings').select('id', { count: 'exact', head: true })
            .eq('project_id', sourceProjectId),
        ]);

        setSourceStats({
          itemsCount: itemsRes.count || 0,
          specsCount: specsRes.count || 0,
          drawingsCount: drawingsRes.count || 0,
        });
      } catch (error) {
        console.error('Failed to load source stats:', error);
      } finally {
        setLoadingStats(false);
      }
    };

    loadStats();
  }, [sourceProjectId]);

  const handleCopy = async () => {
    if (!sourceProjectId) return;

    try {
      const result = await copyMutation.mutateAsync({
        sourceProjectId,
        targetProjectId: projectId,
        copyItems,
        copySpecs,
        copyDrawings,
        skipDuplicates,
        copyLayersPins: copySpecs && copyLayersPins,
        // Pass layer IDs for selective copying
        layerIds: copySpecs && copyLayersPins && !selectAllLayers && selectedLayerIds.length > 0 
          ? selectedLayerIds 
          : null,
        // Spec IDs for selective copying - only when NOT using layer selection
        specIds: copySpecs && !copyLayersPins && !selectAllSpecs && selectedSpecIds.length > 0
          ? selectedSpecIds
          : null,
      });
      
      setCopyResult(result);
    } catch (error) {
      console.error('Copy failed:', error);
    }
  };

  const handleClose = () => {
    setSourceProjectId('');
    setCopyItems(false);
    setCopySpecs(true);
    setCopyDrawings(false);
    setCopyLayersPins(true);
    setSkipDuplicates(true);
    setSourceStats(null);
    setCopyResult(null);
    // Reset layer selection
    setSelectAllLayers(true);
    setSelectedLayerIds([]);
    // NEW: Reset specs selection
    setSpecsSearchTerm('');
    setSelectAllSpecs(false);
    setSelectedSpecIds([]);
    onClose();
  };

  const selectedSourceName = availableProjects.find(p => p.id === sourceProjectId)?.name;
  const hasSelection = copyItems || copySpecs || copyDrawings;
  
  // Validate layer selection when selective copying enabled
  const isLayerSelectionValid = !copySpecs || !copyLayersPins || selectAllLayers || selectedLayerIds.length > 0;
  
  // NEW: Validate spec selection when selective copying enabled
  // When copyLayersPins is true, user is selecting by layers (not individual specs) - skip spec validation
  const isSpecSelectionValid = !copySpecs || copyLayersPins || selectAllSpecs || selectedSpecIds.length > 0;
  
  // Validate spec count limit (only for manual selection, not for "Tất cả" mode)
  const isSpecCountValid = selectAllSpecs || selectedSpecIds.length <= MAX_SELECTABLE_SPECS;

  // Task 5: Multi-project validation - detect if specs from multiple projects are selected
  const selectedSpecProjects = useMemo(() => {
    if (!sourceSpecsData?.specs || selectedSpecIds.length === 0) return [];
    return [...new Set(
      selectedSpecIds
        .map(id => sourceSpecsData.specs.find(s => s.id === id)?.project_id)
        .filter(Boolean)
    )] as string[];
  }, [sourceSpecsData?.specs, selectedSpecIds]);

  const hasMultipleSourceProjects = selectedSpecProjects.length > 1;

  // Preview: calculate exact number of specs/layers to copy
  const copyPreview = useMemo(() => {
    if (!copySpecs) return null;
    
    // Mode A: Copy all specs + layers
    if (copyLayersPins && selectAllLayers) {
      return {
        specsCount: sourceStats?.specsCount ?? 0,
        layersCount: sourceLayers?.length ?? 0,
        mode: 'all' as const,
      };
    }
    
    // Mode B: Copy by selected layers
    if (copyLayersPins && !selectAllLayers && selectedLayerIds.length > 0) {
      const selectedLayers = sourceLayers?.filter(l => selectedLayerIds.includes(l.id)) ?? [];
      const totalSpecs = selectedLayers.reduce((sum, l) => sum + l.specs_count, 0);
      return {
        specsCount: totalSpecs,
        layersCount: selectedLayers.length,
        mode: 'layers' as const,
      };
    }
    
    // Mode C: Copy by selected specs (manual selection)
    if (!copyLayersPins && selectedSpecIds.length > 0) {
      return {
        specsCount: selectedSpecIds.length,
        layersCount: 0,
        mode: 'specs' as const,
      };
    }
    
    return null;
  }, [copySpecs, copyLayersPins, selectAllLayers, selectedLayerIds, selectedSpecIds.length, sourceLayers, sourceStats]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5" />
            Copy dữ liệu từ dự án khác
          </DialogTitle>
          <DialogDescription>
            Copy items, specs, drawings vào: <strong>{projectName}</strong>
          </DialogDescription>
        </DialogHeader>

        {copyResult ? (
          <div className="space-y-4">
            <Alert>
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription>
                <div className="font-medium text-green-600 mb-2">Copy thành công!</div>
                <ul className="text-sm space-y-1">
                  {copyResult.items_copied !== undefined && copyResult.items_copied > 0 && (
                    <li>
                      • Items: {copyResult.items_copied} (+ {copyResult.gates_copied} gates, {copyResult.milestones_copied} milestones)
                    </li>
                  )}
                  {(copyResult.milestones_clamped ?? 0) > 0 && (
                    <li><p className="text-xs text-amber-600">⚠ {copyResult.milestones_clamped} mốc nằm ngoài khoảng item ở dự án nguồn đã được kẹp vào khoảng item.</p></li>
                  )}
                  {copyResult.specs_copied !== undefined && copyResult.specs_copied > 0 && (
                    <li>
                      • Specs: {copyResult.specs_copied} (+ {copyResult.spec_files_copied} files)
                      {(copyResult.layers_copied || 0) > 0 && (
                        <span className="text-muted-foreground ml-1">
                          + {copyResult.layers_copied} layers, {copyResult.pins_copied} pins
                        </span>
                      )}
                    </li>
                  )}
                  {copyResult.drawings_copied !== undefined && copyResult.drawings_copied > 0 && (
                    <li>• Drawings: {copyResult.drawings_copied}</li>
                  )}
                </ul>
                {(copyResult.items_dates_cleared || 0) + (copyResult.milestones_dates_cleared || 0) > 0 && (
                  <div className="text-xs text-amber-700 dark:text-amber-300 mt-2 bg-amber-50 dark:bg-amber-950/30 p-2 rounded">
                    ℹ️ Bạn có thể cập nhật dates sau trong từng item/milestone
                  </div>
                )}
                {copyResult.batch_id && (
                  <div className="text-xs text-muted-foreground mt-2">
                    Batch ID: {copyResult.batch_id.slice(0, 8)}...
                  </div>
                )}
              </AlertDescription>
            </Alert>
            <DialogFooter>
              <Button onClick={handleClose}>Đóng</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Source Project Selection */}
            <div className="space-y-2">
              <Label>Chọn dự án nguồn</Label>
              <div className="flex gap-2">
                <Popover open={projectSelectOpen} onOpenChange={setProjectSelectOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={projectSelectOpen}
                      className="flex-1 justify-between font-normal"
                      disabled={loadingProjects}
                    >
                      {loadingProjects 
                        ? 'Đang tải...' 
                        : selectedSourceName || 'Chọn dự án để copy (hoặc tìm specs từ tất cả)'}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Tìm dự án..." />
                      <CommandList className="max-h-[300px] overflow-y-auto">
                        <CommandEmpty>
                          {projectsError ? (
                            <div className="flex flex-col items-center gap-2 p-2">
                              <span className="text-destructive text-sm">Lỗi tải dự án</span>
                              <Button size="sm" variant="outline" onClick={() => refetchProjects()}>
                                Thử lại
                              </Button>
                            </div>
                          ) : (
                            'Không tìm thấy dự án'
                          )}
                        </CommandEmpty>
                        <CommandGroup>
                          {availableProjects.map((project) => (
                            <CommandItem
                              key={project.id}
                              value={project.name}
                              onSelect={() => {
                                setSourceProjectId(project.id);
                                setProjectSelectOpen(false);
                                // Task 5: Clear spec selection when changing source project
                                setSelectedSpecIds([]);
                                setSpecsSearchTerm('');
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  sourceProjectId === project.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {project.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                
                {/* Clear project button */}
                {sourceProjectId && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setSourceProjectId('');
                      setSourceStats(null);
                      setSelectAllSpecs(false);
                      setSelectedSpecIds([]);
                      setSpecsSearchTerm('');
                    }}
                    className="h-10 w-10 text-muted-foreground hover:text-foreground"
                    aria-label="Bỏ chọn dự án"
                    title="Bỏ chọn dự án để tìm specs từ tất cả dự án"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Stats Preview */}
            {sourceProjectId && (
              <div className="bg-muted/50 rounded-lg p-3 text-sm">
                {loadingStats ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Đang tải thống kê...
                  </div>
                ) : sourceStats ? (
                  <div className="space-y-1">
                    <div className="font-medium">{selectedSourceName}</div>
                    <div className="text-muted-foreground">
                      {sourceStats.itemsCount} items • {sourceStats.specsCount} specs • {sourceStats.drawingsCount} drawings
                    </div>
                  </div>
                ) : null}
              </div>
            )}

            {/* Module Selection */}
            <div className="space-y-3">
              <Label>Chọn dữ liệu cần copy</Label>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="copyItems" 
                  checked={copyItems}
                  disabled={!sourceProjectId}
                  onCheckedChange={(checked) => setCopyItems(checked === true)}
                />
                <Label htmlFor="copyItems" className={cn("font-normal cursor-pointer", !sourceProjectId && "text-muted-foreground")}>
                  Items {sourceStats && `(${sourceStats.itemsCount})`}
                  <span className="text-xs text-muted-foreground ml-1">+ gates, milestones</span>
                </Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="copySpecs" 
                  checked={copySpecs} 
                  onCheckedChange={(checked) => {
                    setCopySpecs(checked === true);
                    // Disable layers/pins if specs unchecked
                    if (!checked) setCopyLayersPins(false);
                  }}
                />
                <Label htmlFor="copySpecs" className="font-normal cursor-pointer">
                  Specs {sourceStats && `(${sourceStats.specsCount})`}
                  <span className="text-xs text-muted-foreground ml-1">+ files</span>
                </Label>
              </div>
              
              {/* Layers & Pins - only visible when Specs is checked */}
              {copySpecs && (
                <div className="ml-6 space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="copyLayersPins" 
                      checked={copyLayersPins} 
                      onCheckedChange={(checked) => {
                        setCopyLayersPins(checked === true);
                        if (!checked) {
                          setSelectAllLayers(true);
                          setSelectedLayerIds([]);
                        }
                      }}
                    />
                    <Label htmlFor="copyLayersPins" className="font-normal cursor-pointer text-sm">
                      Bao gồm Layers & Pins
                      <span className="text-xs text-muted-foreground ml-1">(vị trí trên floorplan)</span>
                    </Label>
                  </div>
                  
                  {/* Layer selection */}
                  {copyLayersPins && sourceLayers && sourceLayers.length > 0 && (
                    <div className="ml-6 border rounded-lg p-3 space-y-2 bg-muted/30">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="selectAllLayers"
                          checked={selectAllLayers}
                      onCheckedChange={(checked) => {
                            setSelectAllLayers(checked === true);
                            if (checked) {
                              setSelectedLayerIds([]);
                            } else {
                              // Auto-uncheck selectAllSpecs when selecting specific layers
                              setSelectAllSpecs(false);
                              setSelectedSpecIds([]);
                            }
                          }}
                        />
                        <Label htmlFor="selectAllLayers" className="font-normal cursor-pointer text-sm">
                          Tất cả layers ({sourceLayers.length})
                        </Label>
                      </div>
                      
                      {!selectAllLayers && (
                        <ScrollArea className="h-32">
                          <div className="space-y-1 pr-3">
                            {sourceLayers.map(layer => (
                              <div key={layer.id} className="flex items-center space-x-2">
                                <Checkbox 
                                  id={`layer-${layer.id}`}
                                  checked={selectedLayerIds.includes(layer.id)}
                                  onCheckedChange={(checked) => {
                                    setSelectedLayerIds(prev => 
                                      checked 
                                        ? [...prev, layer.id]
                                        : prev.filter(id => id !== layer.id)
                                    );
                                  }}
                                />
                                <Label htmlFor={`layer-${layer.id}`} className="font-normal cursor-pointer text-xs">
                                  {layer.name}
                                  <span className="text-muted-foreground ml-1">({layer.specs_count})</span>
                                </Label>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      )}
                      
                      {!selectAllLayers && selectedLayerIds.length === 0 && (
                        <p className="text-xs text-amber-600">Chọn ít nhất 1 layer</p>
                      )}
                    </div>
                  )}
                  
                  {copyLayersPins && loadingLayers && (
                    <div className="ml-6 text-xs text-muted-foreground flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Đang tải layers...
                    </div>
                  )}
                  
                  {copyLayersPins && !loadingLayers && sourceLayers && sourceLayers.length === 0 && (
                    <p className="ml-6 text-xs text-muted-foreground">
                      Không có layers trong dự án nguồn
                    </p>
                  )}
                </div>
              )}

              {/* Specs Search & Selection - Option B: Independent Search (only when NOT using layer selection) */}
              {copySpecs && !copyLayersPins && (
                <div className="ml-6 border rounded-lg p-3 space-y-3 bg-muted/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-medium">Chọn Specs cụ thể</Label>
                      {selectAllSpecs ? (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {sourceProjectId 
                            ? "Copy tất cả specs từ dự án nguồn đã chọn"
                            : "Chọn dự án nguồn để copy tất cả specs"
                          }
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Tìm specs từ <strong>tất cả dự án</strong> bạn có quyền truy cập
                        </p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="selectAllSpecs"
                        checked={selectAllSpecs}
                        disabled={!sourceProjectId}
                        onCheckedChange={(checked) => {
                          setSelectAllSpecs(checked === true);
                          if (checked) {
                            setSelectedSpecIds([]);
                            setSpecsSearchTerm('');
                          }
                        }}
                      />
                      <Label 
                        htmlFor="selectAllSpecs" 
                        className={cn("text-xs cursor-pointer", !sourceProjectId && "text-muted-foreground")}
                      >
                        Tất cả
                      </Label>
                    </div>
                  </div>
                  
                  {!selectAllSpecs && (
                    <>
                      {/* Search Input */}
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Tìm theo code, location, description... (+, &, -)"
                          value={specsSearchTerm}
                          onChange={(e) => setSpecsSearchTerm(e.target.value)}
                          className="pl-8 text-sm h-9"
                        />
                      </div>
                      
                      {/* Count indicator now shown in SpecsVirtualList header */}
                      
                      {/* Preview List with Virtual Scroll */}
                      {loadingSpecs ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Đang tải specs...
                        </div>
                      ) : (
                        <SpecsVirtualList
                          specs={sourceSpecsData?.specs || []}
                          selectedSpecIds={selectedSpecIds}
                          setSelectedSpecIds={setSelectedSpecIds}
                          debouncedSpecsSearch={debouncedSpecsSearch}
                          maxSelectable={MAX_SELECTABLE_SPECS}
                          totalCount={sourceSpecsData?.totalCount}
                          sourceProjectId={sourceProjectId}
                          setSourceProjectId={setSourceProjectId}
                        />
                      )}
                      
                      {/* Selection summary with counter */}
                      {selectedSpecIds.length > 0 && (
                        <div className={cn(
                          "text-xs flex items-center justify-between",
                          selectedSpecIds.length >= MAX_SELECTABLE_SPECS 
                            ? "text-destructive" 
                            : selectedSpecIds.length >= MAX_SELECTABLE_SPECS * 0.9 
                              ? "text-amber-600" 
                              : "text-muted-foreground"
                        )}>
                          <span>
                            Đã chọn: {selectedSpecIds.length}/{MAX_SELECTABLE_SPECS} specs
                            {selectedSpecIds.length >= MAX_SELECTABLE_SPECS && " (đã đạt giới hạn)"}
                          </span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setSelectedSpecIds([])}
                            className="h-6 text-xs px-2"
                          >
                            Bỏ chọn tất cả
                          </Button>
                        </div>
                      )}
                      
                      {/* Warnings - priority order: multi-project > limit > empty */}
                      {hasMultipleSourceProjects && (
                        <p className="text-xs text-destructive flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          Chỉ có thể copy specs từ một dự án. Vui lòng chọn dự án nguồn.
                        </p>
                      )}
                      
                      {!hasMultipleSourceProjects && selectedSpecIds.length >= MAX_SELECTABLE_SPECS && (
                        <p className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/30 p-2 rounded flex items-start gap-1">
                          <AlertCircle className="h-3 w-3 flex-shrink-0 mt-0.5" />
                          Tối đa {MAX_SELECTABLE_SPECS} specs khi chọn thủ công. Nếu cần copy toàn bộ specs của 1 dự án, hãy chọn dự án đó và tick "Tất cả".
                        </p>
                      )}
                      
                      {selectedSpecIds.length === 0 && !hasMultipleSourceProjects && (
                        <p className="text-xs text-amber-600">Chọn ít nhất 1 spec hoặc tick "Tất cả"</p>
                      )}
                    </>
                  )}
                </div>
              )}
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="copyDrawings" 
                  checked={copyDrawings}
                  disabled={!sourceProjectId}
                  onCheckedChange={(checked) => setCopyDrawings(checked === true)}
                />
                <Label htmlFor="copyDrawings" className={cn("font-normal cursor-pointer", !sourceProjectId && "text-muted-foreground")}>
                  Drawings {sourceStats && `(${sourceStats.drawingsCount})`}
                </Label>
              </div>
            </div>

            {/* Options */}
            <div className="border-t pt-3">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="skipDuplicates" 
                  checked={skipDuplicates} 
                  onCheckedChange={(checked) => setSkipDuplicates(checked === true)}
                />
                <Label htmlFor="skipDuplicates" className="font-normal cursor-pointer text-sm">
                  Bỏ qua nếu trùng tên (khuyên dùng)
                </Label>
              </div>
            </div>

            {/* Layer validation hint */}
            {sourceProjectId && copySpecs && copyLayersPins && !selectAllLayers && selectedLayerIds.length === 0 && (
              <p className="text-xs text-amber-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Chọn ít nhất 1 layer hoặc tick "Tất cả" để copy Layers/Pins
              </p>
            )}

            {/* Copy Preview Summary */}
            {copySpecs && copyPreview && copyPreview.specsCount > 0 && (
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                  <Info className="h-4 w-4" />
                  <span className="font-medium text-sm">Preview kết quả copy:</span>
                </div>
                <div className="mt-2 text-sm text-blue-800 dark:text-blue-200 space-y-1">
                  <div className="flex items-center justify-between">
                    <span>Specs:</span>
                    <span className="font-medium">{copyPreview.specsCount}</span>
                  </div>
                  {copyPreview.layersCount > 0 && (
                    <div className="flex items-center justify-between">
                      <span>Layers:</span>
                      <span className="font-medium">{copyPreview.layersCount}</span>
                    </div>
                  )}
                  {copyItems && sourceStats && (
                    <div className="flex items-center justify-between">
                      <span>Items:</span>
                      <span className="font-medium">{sourceStats.itemsCount}</span>
                    </div>
                  )}
                  {copyDrawings && sourceStats && (
                    <div className="flex items-center justify-between">
                      <span>Drawings:</span>
                      <span className="font-medium">{sourceStats.drawingsCount}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Warning */}
            <Alert variant="default" className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-xs text-amber-800 dark:text-amber-200">
                Dữ liệu hiện tại trong dự án đích sẽ KHÔNG bị xóa, chỉ thêm mới. Progress reset về 0; ngày kế hoạch giữ theo dự án nguồn (chỉnh lại sau).
              </AlertDescription>
            </Alert>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Hủy
              </Button>
              <Button 
                onClick={handleCopy} 
                disabled={!sourceProjectId || !hasSelection || !isLayerSelectionValid || !isSpecSelectionValid || !isSpecCountValid || hasMultipleSourceProjects || copyMutation.isPending}
              >
                {copyMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Đang copy...
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy dữ liệu
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
